import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import { billingErrorMessage, useBillingProfile } from '@/src/hooks/queries/billingEngine/useBillingDocuments'
import { useCreateWalletCheckout, useCreditPurchaseOptions } from '@/src/hooks/queries/billingEngine/useBillingEngineWallet'
import { useCurrentTeamId } from '@/src/hooks/useCurrentTeamId'
import type { BillingProfile } from '@/src/types/billingEngine'
import { navigateSafely } from '@/src/utils/safeNavigation'

import { billingDetailsComplete, BillingDetailsFields, BillingDetailsSummary } from './BillingDetailsFields'
import { InvoicePreference } from './BillingDetailsSection'

const QUICK_AMOUNTS = [10, 50, 100, 250, 500]

interface WalletAmountSelectorProps {
  successUrl?: string
  cancelUrl?: string
  recommendedCents?: number
}

// Shared by AddFundsModal and the insufficient-balance prompt. Amount selection
// never redirects: every buyer can request an invoice before continuing.
export const WalletAmountSelector: React.FC<WalletAmountSelectorProps> = ({ successUrl, cancelUrl, recommendedCents }) => {
  const { data: purchaseOptions, isLoading: optionsLoading } = useCreditPurchaseOptions()
  const profileQuery = useBillingProfile()
  const teamId = useCurrentTeamId()
  const client = useQueryClient()
  const createCheckout = useCreateWalletCheckout()
  const recommendedAmount = recommendedCents != null && recommendedCents > 0 ? QUICK_AMOUNTS.find((amount) => amount * 100 >= recommendedCents) : undefined
  const [selected, setSelected] = useState<number | 'custom'>(recommendedAmount || 50)
  const [customAmount, setCustomAmount] = useState('')
  const [invoiceChoice, setInvoiceChoice] = useState<boolean | null>(null)
  const [draft, setDraft] = useState<BillingProfile | null>(null)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const profile = draft || profileQuery.data?.billing_profile
  const invoiceRequested = invoiceChoice ?? profile?.invoice_credit_purchases ?? false
  const editDetails = editing || (profile && !billingDetailsComplete(profile))

  useEffect(() => {
    setDraft(null)
    setInvoiceChoice(null)
    setEditing(false)
    setError(null)
  }, [teamId])

  const processCheckout = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!profile || !purchaseOptions) return
    const dollars = selected === 'custom' ? Number(customAmount) : selected
    const amountCents = Math.round(dollars * 100)
    if (!Number.isFinite(dollars) || amountCents < purchaseOptions.minimum_purchase_amount_cents || amountCents > purchaseOptions.maximum_purchase_amount_cents) {
      setError(`Choose an amount between $${purchaseOptions.minimum_purchase_amount_cents / 100} and $${purchaseOptions.maximum_purchase_amount_cents / 100}.`)
      return
    }
    setError(null)
    try {
      const currentUrl = window.location.origin + window.location.pathname
      const result = await createCheckout.mutateAsync({
        amount_cents: amountCents,
        success_url: successUrl || `${currentUrl}?wallet_checkout_status=pending`,
        cancel_url: cancelUrl || `${currentUrl}?wallet_checkout_status=cancelled`,
        invoice_requested: invoiceRequested,
        ...(invoiceRequested ? { billing_details: profile } : {}),
      })
      client.invalidateQueries(['billing-engine-profile', teamId])
      if (!navigateSafely(result.checkout_url, 'checkout')) setError('Unable to open checkout. Please try again.')
    } catch (error) {
      setError(billingErrorMessage(error))
    }
  }

  if (optionsLoading) return <div className='tw-py-6 tw-text-sm tw-text-gray-500'>Loading options…</div>

  return (
    <form onSubmit={processCheckout} className='tw-space-y-5'>
      <fieldset disabled={createCheckout.isLoading} className='tw-m-0 tw-min-w-0 tw-space-y-5 tw-border-0 tw-p-0'>
        <div>
          <p className='tw-mb-3 tw-text-sm tw-font-semibold tw-text-gray-900'>Choose an amount</p>
          <div className='tw-grid tw-grid-cols-3 tw-gap-2.5'>
            {QUICK_AMOUNTS.map((amount) => {
              const badge = recommendedAmount === amount ? 'Recommended' : recommendedAmount == null && amount === 50 ? 'Popular' : null
              return (
                <button
                  key={amount}
                  type='button'
                  aria-pressed={selected === amount}
                  onClick={() => setSelected(amount)}
                  className={clsx(
                    'tw-flex tw-h-[68px] tw-flex-col tw-items-center tw-justify-center tw-gap-0.5 tw-rounded-xl tw-border tw-border-solid tw-transition-all focus-visible:tw-ring-2 focus-visible:tw-ring-emerald-500',
                    selected === amount
                      ? 'tw-border-emerald-500 tw-bg-emerald-50 tw-text-emerald-700 tw-ring-1 tw-ring-emerald-500'
                      : 'tw-border-gray-200 tw-bg-gray-50 tw-text-gray-900 hover:tw-bg-white',
                  )}
                >
                  <span className={clsx('tw-text-[9px] tw-font-bold tw-uppercase tw-tracking-wider', !badge && 'tw-invisible')}>{badge || 'Popular'}</span>
                  <span className='tw-text-xl tw-font-bold'>${amount}</span>
                </button>
              )
            })}
            <button
              type='button'
              aria-pressed={selected === 'custom'}
              onClick={() => setSelected('custom')}
              className={clsx(
                'tw-h-[68px] tw-rounded-xl tw-border tw-border-solid tw-text-sm tw-font-semibold',
                selected === 'custom' ? 'tw-border-emerald-500 tw-bg-emerald-50 tw-text-emerald-700' : 'tw-border-gray-200 tw-bg-gray-50 tw-text-gray-700',
              )}
            >
              Custom
            </button>
          </div>
          {recommendedAmount != null && <p className='tw-mt-2.5 tw-text-xs tw-text-gray-500'>${recommendedAmount} covers this scan with credit to spare.</p>}
        </div>
        {selected === 'custom' && (
          <label className='tw-block tw-text-sm tw-font-medium tw-text-gray-700'>
            Custom amount (USD)
            <input
              type='number'
              min={(purchaseOptions?.minimum_purchase_amount_cents || 100) / 100}
              max={(purchaseOptions?.maximum_purchase_amount_cents || 1000000) / 100}
              step='0.01'
              required
              autoFocus
              className='tw-mt-2 tw-block tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-gray-300 tw-p-3'
              placeholder='0.00'
              value={customAmount}
              onChange={(event) => setCustomAmount(event.target.value)}
            />
          </label>
        )}
        <div className='tw-space-y-4 tw-border-0 tw-border-t tw-border-solid tw-border-gray-100 tw-pt-4'>
          <label className='tw-flex tw-items-start tw-gap-2 tw-text-sm tw-font-medium tw-text-ink'>
            <input type='checkbox' className='tw-mt-1 tw-accent-emerald-600' checked={invoiceRequested} onChange={(event) => setInvoiceChoice(event.target.checked)} />
            <span>
              Include an invoice with this purchase<span className='tw-mt-1 tw-block tw-text-xs tw-font-normal tw-text-gray-500'>Your payment receipt will still be available.</span>
            </span>
          </label>
          {profileQuery.isError && (
            <p role='alert' className='tw-text-sm tw-text-red-600'>
              Unable to load billing preferences.{' '}
              <button type='button' className='tw-underline' onClick={() => profileQuery.refetch()}>
                Retry
              </button>
            </p>
          )}
          {!profile && !profileQuery.isError && <p className='tw-text-xs tw-text-gray-500'>Loading billing preferences…</p>}
          {invoiceRequested && profile && (
            <>
              {editDetails ? (
                <BillingDetailsFields value={profile} onChange={setDraft} taxIdTypes={profileQuery.data?.tax_id_types || []} />
              ) : (
                <div className='tw-flex tw-items-start tw-justify-between tw-gap-3'>
                  <BillingDetailsSummary profile={profile} />
                  <button type='button' className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-brand-strong' onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>
              )}
              <InvoicePreference value={profile.invoice_credit_purchases} onChange={(value) => setDraft({ ...profile, invoice_credit_purchases: value })} />
              <p className='tw-m-0 tw-text-xs tw-text-gray-500'>Billing details are saved for your team. The invoice is created after payment.</p>
            </>
          )}
        </div>
      </fieldset>
      {error && (
        <p role='alert' className='tw-text-sm tw-text-red-600'>
          {error}
        </p>
      )}
      <button
        type='submit'
        disabled={createCheckout.isLoading || !profile || profileQuery.isError || !purchaseOptions}
        className='tw-w-full tw-rounded-lg tw-border-0 tw-bg-ink tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-ink-soft disabled:tw-opacity-50'
      >
        {createCheckout.isLoading ? 'Opening checkout…' : 'Continue to checkout'}
      </button>
      <div className='tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-gray-500'>
        <span>Credits never expire</span>
        <span>Secure checkout via Stripe</span>
      </div>
    </form>
  )
}

export default WalletAmountSelector
